import { useState, useEffect } from 'react';
import { TextMatchingRule, PredefinedBlock } from './types';
import {
  getPredefinedBlocks,
  shouldUsePredefinedBlocks,
  isCurrencyField,
  isFeeTypeField,
} from './presets';
import {
  createUniquePasteHandler,
  PasteResult,
} from '../../../utils/excelUtils';

export const useTextMatching = (
  open: boolean,
  initialRules: TextMatchingRule[] = [],
  category?: string,
  fieldName?: string
) => {
  const [rules, setRules] = useState<TextMatchingRule[]>([]);
  const [inputValues, setInputValues] = useState<{ [key: number]: string }>({});

  const usePredefinedBlocks = shouldUsePredefinedBlocks(category, fieldName);
  const predefinedBlocks = getPredefinedBlocks(category, fieldName);
  const isCurrency = isCurrencyField(category, fieldName);
  const isFeeType = isFeeTypeField(category, fieldName);

  useEffect(() => {
    if (open) {
      if (isCurrency || isFeeType) {
        // For currency and fee type fields, just use the initial rules (don't add defaults)
        setRules(initialRules.length > 0 ? initialRules : []);
      } else if (usePredefinedBlocks && predefinedBlocks.length > 0) {
        // Initialize with predefined blocks for non-currency fields
        const predefinedRules = predefinedBlocks.map(
          (block: PredefinedBlock) => {
            // Check if we have existing rules for this standard value
            const existingRule = initialRules.find(
              rule => rule.standardValue === block.standardValue
            );

            return {
              standardValue: block.standardValue,
              mappedValues:
                existingRule?.mappedValues || block.defaultVariations || [],
              isPredefined: true,
            };
          }
        );
        setRules(predefinedRules);
      } else {
        // Use regular initialization for non-predefined fields
        setRules(initialRules.length > 0 ? initialRules : []);
      }
      // Clear input values when modal opens
      setInputValues({});
    }
  }, [
    open,
    initialRules,
    usePredefinedBlocks,
    predefinedBlocks,
    isCurrency,
    isFeeType,
  ]);

  const setInputValue = (ruleIndex: number, value: string) => {
    setInputValues(prev => ({
      ...prev,
      [ruleIndex]: value,
    }));
  };

  const getInputValue = (ruleIndex: number): string => {
    return inputValues[ruleIndex] || '';
  };

  const addMappedValue = (ruleIndex: number, value: string): boolean => {
    if (!value.trim()) return false;

    const newRules = [...rules];
    const trimmedValue = value.trim();

    if (!newRules[ruleIndex].mappedValues.includes(trimmedValue)) {
      newRules[ruleIndex].mappedValues.push(trimmedValue);
      setRules(newRules);
      // Clear the input value for this specific rule after adding
      setInputValues(prev => ({
        ...prev,
        [ruleIndex]: '',
      }));
      return true;
    }
    return false;
  };

  const addMultipleMappedValues = (
    ruleIndex: number,
    values: string[]
  ): number => {
    if (!values.length) return 0;

    const newRules = [...rules];
    const validValues = values
      .map(value => value.trim())
      .filter(
        value => value && !newRules[ruleIndex].mappedValues.includes(value)
      );

    if (validValues.length > 0) {
      newRules[ruleIndex].mappedValues.push(...validValues);
      setRules(newRules);
      // Clear the input value after adding
      setInputValues(prev => ({
        ...prev,
        [ruleIndex]: '',
      }));
    }

    return validValues.length;
  };

  const handlePaste = (
    e: React.ClipboardEvent,
    ruleIndex: number
  ): PasteResult => {
    const pasteHandler = createUniquePasteHandler(
      (value: string) => addMappedValue(ruleIndex, value),
      (values: string[]) => addMultipleMappedValues(ruleIndex, values)
    );

    return pasteHandler(e);
  };

  const removeMappedValue = (ruleIndex: number, valueIndex: number) => {
    setRules(prevRules =>
      prevRules.map((rule, index) =>
        index === ruleIndex
          ? {
              ...rule,
              mappedValues: rule.mappedValues.filter(
                (_, i) => i !== valueIndex
              ),
            }
          : rule
      )
    );
  };

  const deleteRule = (ruleIndex: number) => {
    const rule = rules[ruleIndex];
    if (!rule) return;

    // For currency and fee type fields, allow deletion of all entries
    if (isCurrency || isFeeType) {
      setRules(prev => prev.filter((_, index) => index !== ruleIndex));
    } else if (!rule.isPredefined) {
      // For non-currency fields, don't allow deletion of predefined rules
      setRules(prev => prev.filter((_, index) => index !== ruleIndex));
    }
  };

  const addCurrencyRule = (currencyCode: string) => {
    // Check if currency already exists
    const existingRule = rules.find(
      rule => rule.standardValue === currencyCode
    );
    if (existingRule) return;

    const newRule: TextMatchingRule = {
      standardValue: currencyCode,
      mappedValues: [],
      isPredefined: false, // Dynamically added currencies are not predefined
    };

    setRules(prev => [...prev, newRule]);
  };

  const addFeeTypeRule = (feeTypeName: string) => {
    // Check if fee type already exists
    const existingRule = rules.find(rule => rule.standardValue === feeTypeName);
    if (existingRule) return;

    const newRule: TextMatchingRule = {
      standardValue: feeTypeName,
      mappedValues: [],
      isPredefined: false, // Dynamically added fee types are not predefined
    };

    setRules(prev => [...prev, newRule]);
  };

  const handleKeyPress = (e: React.KeyboardEvent, ruleIndex: number) => {
    const value = getInputValue(ruleIndex);
    if (e.key === 'Enter' && value.trim()) {
      addMappedValue(ruleIndex, value);
    }
  };

  const getValidRules = (): TextMatchingRule[] => {
    return rules
      .filter(rule => rule.standardValue.trim() && rule.mappedValues.length > 0)
      .map(({ standardValue, mappedValues }) => ({
        standardValue,
        mappedValues,
      }));
  };

  const hasValidRules = rules.some(
    rule => rule.standardValue.trim() && rule.mappedValues.length > 0
  );

  const getPlaceholderForRule = (ruleIndex: number): string => {
    const rule = rules[ruleIndex];
    if (!rule?.isPredefined) {
      return 'Add variation';
    }

    const predefinedBlock = predefinedBlocks.find(
      block => block.standardValue === rule.standardValue
    );

    return predefinedBlock?.placeholder || 'Add variation';
  };

  return {
    rules,
    getInputValue,
    setInputValue,
    usePredefinedBlocks,
    predefinedBlocks,
    isCurrency,
    isFeeType,
    addMappedValue,
    addMultipleMappedValues,
    removeMappedValue,
    deleteRule,
    addCurrencyRule,
    addFeeTypeRule,
    handleKeyPress,
    handlePaste,
    getValidRules,
    hasValidRules,
    getPlaceholderForRule,
  };
};
